<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">
                <router-link to="/mayar-integration"><i class="fas fa-arrow-left"></i></router-link>
                &nbsp; Template Per Event — {{ integrationName }}
              </h4>
            </div>
            <div class="action-btn">
              <button class="btn btn-success text-white mr-2" @click="seedDefault" :disabled="isSeeding" title="Buat template default untuk event Mayar yang umum">
                <span v-if="isSeeding" class="spinner-border spinner-border-sm mr-1"></span>
                <i v-else class="fas fa-magic"></i>
                Generate Template Default
              </button>
              <button class="btn btn-primary text-white" @click="openAdd">
                <i class="fas fa-plus"></i> Tambah Template
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info">
        <b>Cara kerja:</b>
        Saat webhook masuk dengan event tertentu (misal <code>payment.received</code>), sistem akan cari template di sini dengan event_type sama.
        Kalau ketemu → pakai template ini. Kalau tidak ada → pakai <b>template default</b> di setting integrasi.
        Cocok untuk kasih pesan beda per jenis event (payment vs reminder vs membership expired, dll).
        <br><br>
        💡 <b>Tips:</b> Klik tombol <b>Generate Template Default</b> di pojok kanan atas untuk auto-create 7 template umum (payment.received, payment.reminder, payment.failed, subscription.created, subscription.cancelled, membership.expired, shipper.created). Setelah di-generate, bisa edit sesuai kebutuhan.
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else-if="templates.length === 0" class="text-center text-muted p-4">
              <p>Belum ada template per event. Klik <b>Tambah Template</b> untuk mulai.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="bg-light">
                  <tr>
                    <th>Event Type</th>
                    <th>Customer Template</th>
                    <th>Admin Template</th>
                    <th>Status</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in templates" :key="t.id">
                    <td><code>{{ t.event_type }}</code></td>
                    <td><small>{{ truncate(t.template_customer, 80) }}</small></td>
                    <td><small>{{ truncate(t.template_admin, 80) }}</small></td>
                    <td>
                      <span v-if="t.is_active === 1" class="badge badge-success">Aktif</span>
                      <span v-else class="badge badge-secondary">Nonaktif</span>
                    </td>
                    <td class="text-right">
                      <button class="btn btn-sm btn-primary" @click="openEdit(t)" title="Edit">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button class="btn btn-sm btn-danger ml-1" @click="del(t)" title="Hapus">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Form -->
      <modal name="event_tpl_form" :height="'auto'" :adaptive="true" :scrollable="true" :width="'80%'">
        <div style="margin: 20px">
          <h4>{{ editId ? 'Edit' : 'Tambah' }} Template Per Event</h4>
          <br>
          <form @submit.prevent="save">
            <label>Event Type <span class="text-danger">*</span></label>
            <input v-model="form.event_type" class="form-control" required
              placeholder="payment.received">
            <small class="text-muted">
              Contoh: <code>payment.received</code>, <code>payment.reminder</code>, <code>membership.expired</code>, <code>subscription.created</code>.
              Cek <b>Webhook Log</b> untuk lihat event type yang Mayar kirim.
            </small>
            <br><br>

            <div class="row">
              <div class="col-md-6">
                <label>Template Pesan ke Customer</label>
                <textarea v-model="form.template_customer" class="form-control" rows="10"
                  placeholder="Halo {name}..."></textarea>
              </div>
              <div class="col-md-6">
                <label>Template Pesan ke Admin</label>
                <textarea v-model="form.template_admin" class="form-control" rows="10"
                  placeholder="Event {event} masuk..."></textarea>
              </div>
            </div>
            <br>

            <div class="alert alert-secondary" style="font-size: 12px;">
              <b>Placeholder:</b> {event}, {name}, {email}, {phone}, {amount}, {product_name}, {product_type}, {transaction_id}, {coupon_code}, {created_at}, {status}, {merchant_id}
            </div>

            <label>
              <input type="checkbox" v-model="form.is_active_bool"> &nbsp;Aktif
            </label>
            <br><br>

            <div class="text-right">
              <button type="button" class="btn btn-secondary" @click="$modal.hide('event_tpl_form')">Batal</button>
              <button type="submit" class="btn btn-primary ml-2" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm mr-2"></span>
                {{ editId ? 'Simpan' : 'Tambah' }}
              </button>
            </div>
          </form>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const COMMON_EVENTS = [
  "payment.received",
  "payment.reminder",
  "payment.failed",
  "subscription.created",
  "subscription.cancelled",
  "membership.expired",
  "shipper.created",
];

export default {
  data() {
    return {
      integrationId: null,
      integrationName: "...",
      templates: [],
      isLoading: true,
      isSaving: false,
      isSeeding: false,
      editId: null,
      form: {
        event_type: "",
        template_customer: "",
        template_admin: "",
        is_active_bool: true,
      },
      commonEvents: COMMON_EVENTS,
    };
  },
  mounted() {
    this.integrationId = parseInt(this.$route.params.id);
    this.loadIntegration();
    this.loadData();
  },
  methods: {
    async loadIntegration() {
      try {
        const res = await axios.get("mayar-integration/" + this.integrationId);
        this.integrationName = res.data.data.integration.name;
      } catch (err) {}
    },
    async loadData() {
      this.isLoading = true;
      try {
        const res = await axios.get(
          "mayar-integration/" + this.integrationId + "/event-templates"
        );
        this.templates = res.data.data.templates || [];
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal memuat data");
      } finally {
        this.isLoading = false;
      }
    },
    openAdd() {
      this.editId = null;
      this.form = {
        event_type: "",
        template_customer: "",
        template_admin: "",
        is_active_bool: true,
      };
      this.$modal.show("event_tpl_form");
    },
    openEdit(t) {
      this.editId = t.id;
      this.form = {
        event_type: t.event_type,
        template_customer: t.template_customer || "",
        template_admin: t.template_admin || "",
        is_active_bool: t.is_active === 1,
      };
      this.$modal.show("event_tpl_form");
    },
    async save() {
      this.isSaving = true;
      try {
        const payload = {
          event_type: this.form.event_type.trim(),
          template_customer: this.form.template_customer || null,
          template_admin: this.form.template_admin || null,
          is_active: this.form.is_active_bool ? 1 : 0,
        };
        if (this.editId) {
          await axios.put(
            "mayar-integration/" + this.integrationId + "/event-templates/" + this.editId,
            payload
          );
          this.$toast.success("Template tersimpan");
        } else {
          await axios.post(
            "mayar-integration/" + this.integrationId + "/event-templates",
            payload
          );
          this.$toast.success("Template ditambahkan");
        }
        this.$modal.hide("event_tpl_form");
        this.loadData();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal menyimpan");
      } finally {
        this.isSaving = false;
      }
    },
    async seedDefault() {
      if (this.templates.length > 0) {
        if (!confirm("Sudah ada template. Generate akan SKIP yang sudah ada (tidak overwrite). Lanjut?")) return;
      }
      this.isSeeding = true;
      try {
        const res = await axios.post(
          "mayar-integration/" + this.integrationId + "/event-templates/seed"
        );
        this.$toast.success(res.data.message || "Template default berhasil di-generate");
        this.loadData();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal generate template");
      } finally {
        this.isSeeding = false;
      }
    },
    async del(t) {
      if (!confirm("Yakin hapus template untuk event '" + t.event_type + "'?")) return;
      try {
        await axios.delete(
          "mayar-integration/" + this.integrationId + "/event-templates/" + t.id
        );
        this.$toast.success("Template dihapus");
        this.loadData();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal menghapus");
      }
    },
    truncate(s, n) {
      if (!s) return "(kosong, fallback ke template default)";
      return s.length > n ? s.slice(0, n) + "..." : s;
    },
  },
};
</script>

<style scoped>
code {
  background: #f1f1f1;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #d63384;
}
</style>
